import Loader from "atoms/loaders/loader";
import AnnexRecord from "models/view-models/annex-record";
import CurrentPublicationEntityRecord from "models/view-models/atoms/current-publication-entity-record";
import AnnexTableOfContentsRecord from "models/view-models/table-of-contents/annex-table-of-contents-record";
import UnorderedList from "molecules/lists/unordered-list";
import CaapsItemTitleAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/caaps-title-area-within-panel";
import PublicationContentAreaWithinPanel from "organisms/publication-content/notebook-reference-panel/publication-content-area-within-panel";
import useVirtualTableOfContents from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
import React, { useMemo, useRef } from "react";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import { CollectionUtils } from "utilities/collection-utils";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import useUpdateAtomEffect from "utilities/hooks/atoms/use-update-atom-value-effect";
import useAnnexesQuery from "utilities/hooks/domain/publications/annexes/use-annexes-query";
import useSectionsQuery from "utilities/hooks/domain/publications/annexes/use-sections-query";
import useBookviewTableOfContents from "utilities/hooks/domain/publications/use-bookview-table-of-contents";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import useErrors from "utilities/hooks/use-errors";
import { useLocalization } from "utilities/hooks/use-localization";
import NumberUtils from "utilities/number-utils";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface AnnexContentWithinPanelProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const cssBaseClassName = "c-publication-page-layout";

// #endregion Constants

const AnnexContentWithinPanel: React.FunctionComponent<
    AnnexContentWithinPanelProps
> = (_: AnnexContentWithinPanelProps) => {
    const { publication } = useCurrentPublication();
    const { currentTocItem } = useVirtualTableOfContents();
    const {
        nfpaLabel,
        code,
        edition,
        id: annexId,
    } = {
        nfpaLabel: currentTocItem?.nfpaLabel,
        code: publication.code,
        edition: publication.edition,
        id: currentTocItem?.annexId,
    };
    const { t } = useLocalization();
    const annexContentRef = useRef<HTMLDivElement>(null);
    const { tableOfContents, loading: tocLoading } =
        useBookviewTableOfContents();

    // -----------------------------------------------------------------------------------------
    // #region Service Hooks
    // -----------------------------------------------------------------------------------------

    const {
        loaded: annexesLoaded,
        resultObject: annexes,
        errors: annexesError,
    } = useAnnexesQuery({
        code,
        edition,
        publicationId: publication?.id,
        publication: publication,
    });

    // Find the active annex.
    const parsedAnnexId = NumberUtils.parseInt(annexId);
    const annex = useMemo(() => {
        if (
            (parsedAnnexId == null && StringUtils.isEmpty(nfpaLabel)) ||
            CollectionUtils.isEmpty(annexes)
        ) {
            return;
        }

        const byLabel = (annex: AnnexRecord) => annex.nfpaLabel === nfpaLabel;
        const byId = (annex: AnnexRecord) => annex.id === parsedAnnexId;
        const filter = parsedAnnexId == null ? byLabel : byId;
        const annex = annexes.find(filter);

        if (annex == null) {
            return;
        }

        return annex;
    }, [parsedAnnexId, nfpaLabel, annexes]);

    const annexTOC =
        tableOfContents.annexes?.find((a) => a.id === annex?.id) ??
        new AnnexTableOfContentsRecord();
    const hasGroupedAnnexes = CollectionUtils.hasValues(annexTOC?.annexGroups);

    const {
        resultObject: sections,
        loaded: sectionsLoaded,
        errors: sectionErrors,
    } = useSectionsQuery(annex?.id, annex?.publicationId);

    // #endregion Service Hooks

    const contentLoaded = sectionsLoaded && !tocLoading && annex != null;
    const loaderText = t("loadingItem", { item: t("annex") });

    // If annex groups exist, sections and sub-sections should not be displayed
    const filteredSections = useMemo(() => {
        return hasGroupedAnnexes || sections == null || nfpaLabel == null
            ? []
            : sections.map((s) => s.with({ publication }));
    }, [hasGroupedAnnexes, sections, nfpaLabel, publication]);

    useUpdateAtomEffect({
        atom: CurrentPublicationEntityAtom,
        enabled: annex != null && annexesLoaded,
        value: new CurrentPublicationEntityRecord({
            entityId: annex?.id!,
            entityTitle: annex?.getDisplayLabel() ?? "",
            selected: true,
            type: "Annex",
        }),
    });

    const errors = useErrors(annexesError, sectionErrors);
    useErrorBoundary(errors);

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    const titleArea = useMemo(() => {
        if (contentLoaded)
            return (
                <CaapsItemTitleAreaWithinPanel
                    ref={annexContentRef}
                    externalId={annex!.externalId}
                    publication={publication}
                    titleClassName={""}
                    record={annex!}
                    publicationComponentType={PublicationComponentType.Annex}>
                    <div className={`${cssBaseClassName}__body`}>
                        {annex?.getBody()}
                    </div>
                </CaapsItemTitleAreaWithinPanel>
            );
    }, [annex, contentLoaded, publication]);

    return (
        <React.Fragment>
            {
                // if
                CollectionUtils.hasValues(errors) && (
                    <UnorderedList
                        cssClassName={`${cssBaseClassName}__errors`}
                        listItems={errors}
                    />
                )
            }

            {!contentLoaded ? (
                <Loader accessibleText={loaderText} />
            ) : (
                <>
                    <PublicationContentAreaWithinPanel
                        titleArea={titleArea}
                        sections={filteredSections!}
                        externalId={annex?.externalId}
                    />
                </>
            )}
        </React.Fragment>
    );

    // #endregion Render
};

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default AnnexContentWithinPanel;

// #endregion Exports
